package at.theatervogelweide.admin.contract.news

import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.ImageDetails
import at.theatervogelweide.admin.contract.VersionedId
import at.theatervogelweide.admin.contract.isImageUuid
import at.theatervogelweide.admin.contract.validate
import at.theatervogelweide.domain.NewsVisibility
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.serialization.Serializable

// All timestamps are in UTC, as stored in the database; NewsPeriod converts them from and to the days people enter.

/** A row of the list. */
@Serializable
data class NewsListEntry(
    val id: Long,
    val version: Long,
    val title: String,
    val activeFrom: LocalDateTime?,
    val activeTo: LocalDateTime?,
    /** The author's person; `null` once the account is deleted or without a person, as on the public site (#66). */
    val authorName: String?,
    /** The first image, which the public site shows as the main image. */
    val mainImageUuid: String?,
) {
    val versionedId: VersionedId get() = VersionedId(id, version)

    fun visibility(now: LocalDateTime): NewsVisibility = NewsVisibility.of(activeFrom, activeTo, now)
}

/** A saved item, as the editor loads it. */
@Serializable
data class NewsEditor(
    val id: Long,
    val version: Long,
    /** As in [NewsListEntry.authorName]. */
    val authorName: String?,
    val draft: NewsDraft,
) {
    val versionedId: VersionedId get() = VersionedId(id, version)
}

/** What the editor creates and saves. */
@Serializable
data class NewsDraft(
    val title: String = "",
    /** Markdown, which the public site renders. */
    val text: String = "",
    /** "Veröffentlicht ab", whose day the website shows as the date (#151); required for a new item, see [validate]. */
    val activeFrom: LocalDateTime? = null,
    /** "Veröffentlicht bis". */
    val activeTo: LocalDateTime? = null,
    /** In the order the public site shows them; the first is the main image. */
    val imageUuids: List<String> = emptyList(),
    val publishUnderMyName: Boolean = false,
    /** The details of the images (#105); one without an entry has none, and entries of other images are ignored. */
    val imageDetails: Map<String, ImageDetails> = emptyMap(),
) {
    /** [new] for an item being created, which needs [activeFrom]; older items without one can still be saved. */
    fun validate(new: Boolean = false): List<FieldError> = validate {
        field(TITLE, title) { required(); maxLength(255) }
        field(TEXT, text) { maxLength(MAX_TEXT_LENGTH) }
        check(ACTIVE_FROM, !new || activeFrom != null) { "Bitte ein Datum eingeben." }
        check(ACTIVE_TO, activeFrom == null || activeTo == null || activeTo > activeFrom) {
            "Bitte ein Ende nach dem Beginn wählen."
        }
        check(IMAGES, imageUuids.all(::isImageUuid) && imageUuids.distinct().size == imageUuids.size) {
            "Die Bilder sind ungültig. Bitte neu laden."
        }
        // the details of images the draft no longer has are left out, like when saving
        imageDetails.filterKeys { it in imageUuids }.values.forEach { errors += it.validate(IMAGES) }
    }

    companion object {
        /** The size of `news_item.description`. */
        const val MAX_TEXT_LENGTH = 5000

        const val TITLE = "title"
        const val TEXT = "text"
        const val ACTIVE_FROM = "activeFrom"
        const val ACTIVE_TO = "activeTo"
        const val IMAGES = "images"

        /** The "Veröffentlicht ab" a new item starts with (#151): [now] in UTC, to the minute. */
        fun publishedNow(now: LocalDateTime): LocalDateTime = LocalDateTime(now.date, LocalTime(now.hour, now.minute))
    }
}
